import { useState } from 'react';
import { useSearch } from '@tanstack/react-router';
import Page from '@/components/page/Page';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  useEDI844SubmissionsList,
  useEDI844Submission,
  useEDI844SubmissionActivities,
} from '@/api/rpc/hooks/ship-and-debit';
import type {
  EDI844Submission,
  EDI844SubmissionActivity,
  EDI844SubmissionStatus,
  EDI844DeliveryMethod,
} from '@erp/rpc';
import {
  EnvelopeIcon,
  FileTextIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  WarningCircleIcon,
  ChatIcon,
  PaperPlaneRightIcon,
  FileIcon,
} from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';

type SearchParams = {
  tab?: 'pending' | 'accepted' | 'rejected';
  page?: number;
  pageSize?: number;
  submissionId?: string;
};

function formatCurrency(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return '-';
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(numValue);
}

function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getStatusBadgeVariant(
  status: EDI844SubmissionStatus,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'accepted':
      return 'default';
    case 'rejected':
      return 'destructive';
    case 'partial':
      return 'secondary';
    case 'sent':
    case 'pending':
      return 'outline';
    default:
      return 'secondary';
  }
}

function getDeliveryMethodIcon(method: EDI844DeliveryMethod) {
  return method === 'edi' ? (
    <FileTextIcon className="h-4 w-4" />
  ) : (
    <EnvelopeIcon className="h-4 w-4" />
  );
}

function getActivityIcon(activityType: string) {
  switch (activityType) {
    case 'created':
      return <FileIcon className="h-4 w-4 text-blue-500" />;
    case 'sent_via_edi':
    case 'sent_via_email':
      return <PaperPlaneRightIcon className="h-4 w-4 text-green-500" />;
    case 'response_received':
      return <EnvelopeIcon className="h-4 w-4 text-purple-500" />;
    case 'line_accepted':
      return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
    case 'line_rejected':
      return <XCircleIcon className="h-4 w-4 text-red-500" />;
    case 'line_partial':
      return <WarningCircleIcon className="h-4 w-4 text-yellow-500" />;
    case 'disputed':
    case 'dispute_response':
      return <WarningCircleIcon className="h-4 w-4 text-orange-500" />;
    case 'comment_added':
      return <ChatIcon className="h-4 w-4 text-gray-500" />;
    case 'status_changed':
      return <ClockIcon className="h-4 w-4 text-blue-500" />;
    case 'vdm_created':
      return <FileTextIcon className="h-4 w-4 text-green-500" />;
    default:
      return <ClockIcon className="h-4 w-4 text-gray-400" />;
  }
}

// Skeleton components
function SubmissionTableSkeleton() {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Submission #</TableHead>
            <TableHead>Supplier</TableHead>
            <TableHead>Division</TableHead>
            <TableHead>Location</TableHead>
            <TableHead>Method</TableHead>
            <TableHead className="text-right">Lines</TableHead>
            <TableHead className="text-right">Claimed</TableHead>
            <TableHead className="text-right">Accepted</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Sent</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 5 }).map((_, i) => (
            <TableRow key={i}>
              {Array.from({ length: 10 }).map((_, j) => (
                <TableCell key={j}>
                  <Skeleton className="h-4 w-20" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ActivityTimelineSkeleton() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex gap-3">
          <Skeleton className="h-8 w-8 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/4" />
          </div>
        </div>
      ))}
    </div>
  );
}

// Submission table component
function SubmissionTable({
  submissions,
  isLoading,
  emptyMessage,
  onRowClick,
}: {
  submissions: EDI844Submission[];
  isLoading: boolean;
  emptyMessage: string;
  onRowClick?: (submission: EDI844Submission) => void;
}) {
  if (isLoading) {
    return <SubmissionTableSkeleton />;
  }

  if (submissions.length === 0) {
    return (
      <div className="rounded-md border p-8 text-center text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Submission #</TableHead>
            <TableHead>Supplier</TableHead>
            <TableHead>Division</TableHead>
            <TableHead>Location</TableHead>
            <TableHead>Method</TableHead>
            <TableHead className="text-right">Lines</TableHead>
            <TableHead className="text-right">Claimed</TableHead>
            <TableHead className="text-right">Accepted</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Sent</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {submissions.map((submission) => (
            <TableRow
              key={submission.id}
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => onRowClick?.(submission)}
            >
              <TableCell className="font-mono text-sm">
                #{submission.submissionNumber}
              </TableCell>
              <TableCell>
                <div
                  className="max-w-[150px] truncate"
                  title={submission.supplier?.name}
                >
                  {submission.supplier?.name ?? '-'}
                </div>
              </TableCell>
              <TableCell className="font-mono text-sm">
                {submission.divisionCode}
              </TableCell>
              <TableCell>
                <div
                  className="max-w-[120px] truncate"
                  title={
                    submission.sellingLocationName ??
                    submission.sellingLocationCode
                  }
                >
                  {submission.sellingLocationName ??
                    submission.sellingLocationCode}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  {getDeliveryMethodIcon(submission.deliveryMethod)}
                  <span className="text-xs uppercase">
                    {submission.deliveryMethod}
                  </span>
                </div>
              </TableCell>
              <TableCell className="text-right font-mono">
                {submission.lineCount ?? '-'}
              </TableCell>
              <TableCell className="text-right font-mono">
                {formatCurrency(submission.totalClaimedAmount)}
              </TableCell>
              <TableCell className="text-right font-mono">
                {formatCurrency(submission.totalAcceptedAmount)}
              </TableCell>
              <TableCell>
                <Badge variant={getStatusBadgeVariant(submission.status)}>
                  {submission.status}
                </Badge>
              </TableCell>
              <TableCell className="text-muted-foreground">
                {formatDate(submission.sentAt)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// Activity timeline component
function ActivityTimeline({
  activities,
  isLoading,
}: {
  activities: EDI844SubmissionActivity[];
  isLoading: boolean;
}) {
  if (isLoading) {
    return <ActivityTimelineSkeleton />;
  }

  if (activities.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-8">
        No activity recorded yet
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {activities.map((activity, index) => (
        <div key={activity.id} className="flex gap-3">
          <div className="flex flex-col items-center">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
              {getActivityIcon(activity.activityType)}
            </div>
            {index < activities.length - 1 && (
              <div className="w-px flex-1 bg-border mt-2" />
            )}
          </div>
          <div className="flex-1 pb-4">
            <p className="text-sm font-medium">{activity.description}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-muted-foreground">
                {formatDateTime(activity.eventAt)}
              </span>
              {activity.user && (
                <>
                  <span className="text-xs text-muted-foreground">•</span>
                  <span className="text-xs text-muted-foreground">
                    {activity.user.name}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Submission lines table
function SubmissionLinesTable({
  lines,
  isLoading,
}: {
  lines: Array<{
    id: string;
    claimedAmount: string;
    acceptedAmount?: string | null;
    lineStatus: string;
    rejectionReason?: string | null;
    sourceTransaction?: {
      id: string;
      invoiceNumber?: string | null;
      customerPoNumber?: string | null;
      itemInternalCode?: string | null;
      description?: string | null;
      shippedQuantity?: number | null;
      claimAmount?: number | null;
      tradingPartner?: string | null;
      agreementNumber?: string | null;
    };
  }>;
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (lines.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-4 text-sm">
        No transaction lines found
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[60px]">#</TableHead>
            <TableHead>Invoice #</TableHead>
            <TableHead>Item Code</TableHead>
            <TableHead className="max-w-[200px]">Description</TableHead>
            <TableHead className="text-right">Qty</TableHead>
            <TableHead className="text-right">Claimed</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {lines.map((line, index) => (
            <TableRow key={line.id}>
              <TableCell className="font-mono text-xs">{index + 1}</TableCell>
              <TableCell className="font-mono text-xs">
                {line.sourceTransaction?.invoiceNumber ?? '-'}
              </TableCell>
              <TableCell className="font-mono text-xs">
                {line.sourceTransaction?.itemInternalCode ?? '-'}
              </TableCell>
              <TableCell
                className="text-xs max-w-[200px] truncate"
                title={line.sourceTransaction?.description ?? ''}
              >
                {line.sourceTransaction?.description ?? '-'}
              </TableCell>
              <TableCell className="text-right font-mono text-xs">
                {line.sourceTransaction?.shippedQuantity ?? '-'}
              </TableCell>
              <TableCell className="text-right font-mono text-xs">
                {formatCurrency(line.claimedAmount)}
              </TableCell>
              <TableCell>
                <Badge
                  variant={getLineStatusBadgeVariant(line.lineStatus)}
                  className="text-xs"
                >
                  {line.lineStatus}
                </Badge>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function getLineStatusBadgeVariant(
  status: string,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'accepted':
      return 'default';
    case 'rejected':
      return 'destructive';
    case 'partial':
      return 'secondary';
    case 'pending':
      return 'outline';
    default:
      return 'secondary';
  }
}

// Submission detail modal
function SubmissionDetailModal({
  submissionId,
  open,
  onClose,
}: {
  submissionId: string | undefined;
  open: boolean;
  onClose: () => void;
}) {
  const { submission, isLoading: isLoadingSubmission } = useEDI844Submission(
    submissionId,
    {
      includeLines: true,
      includeSupplier: true,
      includeLineSourceTransactions: true,
    },
    open,
  );

  const { activities, isLoading: isLoadingActivities } =
    useEDI844SubmissionActivities(submissionId, {}, open);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        className="!max-w-[90vw] sm:!max-w-[1200px] max-h-[90vh]"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {isLoadingSubmission ? (
              <Skeleton className="h-6 w-48" />
            ) : submission ? (
              <>Submission #{submission.submissionNumber}</>
            ) : (
              'Submission Details'
            )}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-100px)]">
          {isLoadingSubmission ? (
            <div className="space-y-4">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : submission ? (
            <div className="space-y-6 pr-4">
              {/* Summary and Activity side by side */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Summary Card */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">
                      Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Supplier</span>
                      <span>{submission.supplier?.name ?? '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Division</span>
                      <span className="font-mono">
                        {submission.divisionCode}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Location</span>
                      <span>
                        {submission.sellingLocationName ??
                          submission.sellingLocationCode}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Delivery Method
                      </span>
                      <span className="flex items-center gap-1">
                        {getDeliveryMethodIcon(submission.deliveryMethod)}
                        <span className="uppercase">
                          {submission.deliveryMethod}
                        </span>
                      </span>
                    </div>
                    <Separator className="my-2" />
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Total Claimed
                      </span>
                      <span className="font-mono font-medium">
                        {formatCurrency(submission.totalClaimedAmount)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Total Accepted
                      </span>
                      <span className="font-mono font-medium">
                        {formatCurrency(submission.totalAcceptedAmount)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Status</span>
                      <Badge variant={getStatusBadgeVariant(submission.status)}>
                        {submission.status}
                      </Badge>
                    </div>
                    <Separator className="my-2" />
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Created</span>
                      <span>{formatDateTime(submission.createdAt)}</span>
                    </div>
                    {submission.sentAt && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Sent</span>
                        <span>{formatDateTime(submission.sentAt)}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Activity Timeline */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">
                      Activity Timeline
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[250px]">
                      <ActivityTimeline
                        activities={activities}
                        isLoading={isLoadingActivities}
                      />
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>

              {/* Submission Lines */}
              <div>
                <h3 className="text-sm font-medium mb-3">
                  Submitted Transactions ({submission.lines?.length ?? 0})
                </h3>
                <SubmissionLinesTable
                  lines={[...(submission.lines ?? [])].sort(
                    (a, b) =>
                      parseFloat(b.claimedAmount || '0') -
                      parseFloat(a.claimedAmount || '0'),
                  )}
                  isLoading={false}
                />
              </div>
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-8">
              Submission not found
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

// Pending submissions tab (sent, awaiting response)
function PendingSubmissionsTab({
  onSubmissionClick,
}: {
  onSubmissionClick: (submission: EDI844Submission) => void;
}) {
  const { submissions, isLoading, pagination } = useEDI844SubmissionsList({
    status: ['sent', 'pending', 'draft'],
    includeSupplier: true,
    limit: 50,
  });

  // Calculate totals
  const totals = submissions.reduce(
    (acc: { claimed: number; count: number }, s) => ({
      claimed: acc.claimed + parseFloat(s.totalClaimedAmount || '0'),
      count: acc.count + 1,
    }),
    { claimed: 0, count: 0 },
  );

  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pending Submissions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totals.count}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Claimed Amount
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono">
              {formatCurrency(totals.claimed)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Records
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pagination?.total ?? 0}</div>
          </CardContent>
        </Card>
      </div>

      <SubmissionTable
        submissions={submissions}
        isLoading={isLoading}
        emptyMessage="No pending 844s found"
        onRowClick={onSubmissionClick}
      />
    </div>
  );
}

// Accepted submissions tab
function AcceptedSubmissionsTab({
  onSubmissionClick,
}: {
  onSubmissionClick: (submission: EDI844Submission) => void;
}) {
  const { submissions, isLoading, pagination } = useEDI844SubmissionsList({
    status: ['accepted', 'partial'],
    includeSupplier: true,
    limit: 50,
  });

  // Calculate totals
  const totals = submissions.reduce(
    (acc: { claimed: number; accepted: number; count: number }, s) => ({
      claimed: acc.claimed + parseFloat(s.totalClaimedAmount || '0'),
      accepted: acc.accepted + parseFloat(s.totalAcceptedAmount || '0'),
      count: acc.count + 1,
    }),
    { claimed: 0, accepted: 0, count: 0 },
  );

  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Accepted Submissions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totals.count}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Claimed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono">
              {formatCurrency(totals.claimed)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Accepted
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono text-green-600">
              {formatCurrency(totals.accepted)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Acceptance Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {totals.claimed > 0
                ? `${((totals.accepted / totals.claimed) * 100).toFixed(1)}%`
                : '-'}
            </div>
          </CardContent>
        </Card>
      </div>

      <SubmissionTable
        submissions={submissions}
        isLoading={isLoading}
        emptyMessage="No accepted submissions found"
        onRowClick={onSubmissionClick}
      />
    </div>
  );
}

// Rejected submissions tab
function RejectedSubmissionsTab({
  onSubmissionClick,
}: {
  onSubmissionClick: (submission: EDI844Submission) => void;
}) {
  const { submissions, isLoading, pagination } = useEDI844SubmissionsList({
    status: ['rejected', 'disputed'],
    includeSupplier: true,
    limit: 50,
  });

  // Calculate totals
  const totals = submissions.reduce(
    (acc: { claimed: number; count: number }, s) => ({
      claimed: acc.claimed + parseFloat(s.totalClaimedAmount || '0'),
      count: acc.count + 1,
    }),
    { claimed: 0, count: 0 },
  );

  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Rejected Submissions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totals.count}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Rejected Amount
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono text-red-600">
              {formatCurrency(totals.claimed)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Records
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pagination?.total ?? 0}</div>
          </CardContent>
        </Card>
      </div>

      <SubmissionTable
        submissions={submissions}
        isLoading={isLoading}
        emptyMessage="No rejected submissions found"
        onRowClick={onSubmissionClick}
      />
    </div>
  );
}

export function ShipAndDebit844sPage() {
  const searchParams = useSearch({
    from: '/authenticated/ship-and-debit/844-and-849',
  }) as SearchParams;

  const [activeTab, setActiveTab] = useState<
    'pending' | 'accepted' | 'rejected'
  >(searchParams.tab ?? 'pending');

  const [selectedSubmissionId, setSelectedSubmissionId] = useState<
    string | undefined
  >(searchParams.submissionId);

  function handleSubmissionClick(submission: EDI844Submission) {
    setSelectedSubmissionId(submission.id);
  }

  function handleSheetClose() {
    setSelectedSubmissionId(undefined);
  }

  return (
    <Page title="844s & 849s">
      <div className="space-y-4">
        <Tabs
          value={activeTab}
          onValueChange={(value) =>
            setActiveTab(value as 'pending' | 'accepted' | 'rejected')
          }
        >
          <TabsList>
            <TabsTrigger value="pending">844s Pending</TabsTrigger>
            <TabsTrigger value="accepted">844s Accepted</TabsTrigger>
            <TabsTrigger value="rejected">844s Rejected</TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-4">
            <PendingSubmissionsTab onSubmissionClick={handleSubmissionClick} />
          </TabsContent>

          <TabsContent value="accepted" className="mt-4">
            <AcceptedSubmissionsTab onSubmissionClick={handleSubmissionClick} />
          </TabsContent>

          <TabsContent value="rejected" className="mt-4">
            <RejectedSubmissionsTab onSubmissionClick={handleSubmissionClick} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Submission Detail Modal */}
      <SubmissionDetailModal
        submissionId={selectedSubmissionId}
        open={Boolean(selectedSubmissionId)}
        onClose={handleSheetClose}
      />
    </Page>
  );
}
